import { Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AlertsService } from '../services/alerts.service';
import { ProjectService } from '../services/project.service';
import { UserAuthService } from '../UserAuth.service';
import { alertRoute } from '../services/alert-deep-link';
import { Alert, AlertStateFilter } from '../models/alert';

/**
 * Alert history page: filter tabs (Open / Acknowledged / Resolved / All),
 * table of alerts with per-row acknowledge and bulk "Acknowledge all".
 */
@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.scss'],
})
export class AlertsComponent implements OnInit, OnDestroy {
  readonly tabs: { key: AlertStateFilter; label: string }[] = [
    { key: 'open', label: 'Open' },
    { key: 'acknowledged', label: 'Acknowledged' },
    { key: 'resolved', label: 'Resolved' },
    { key: 'all', label: 'All' },
  ];

  activeTab: AlertStateFilter = 'open';
  alerts: Alert[] = [];
  loading = false;
  acknowledging = false;
  /** administrator or SuperAdmin, from the server's CheckUserExists roles: DataServices' own rule. */
  isAdmin = false;
  /** The project open in the app, or '' when none is. */
  project = '';

  private destroyed$ = new Subject<void>();

  constructor(
    private alertsService: AlertsService,
    private router: Router,
    private auth: UserAuthService,
    private projects: ProjectService
  ) {}

  ngOnInit(): void {
    this.auth.isAdmin$.pipe(takeUntil(this.destroyed$)).subscribe((admin) => (this.isAdmin = admin));
    this.projects.selectedProject.pipe(takeUntil(this.destroyed$)).subscribe((p) => (this.project = p ?? ''));
    this.loadAlerts();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  selectTab(tab: AlertStateFilter): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.loadAlerts();
  }

  loadAlerts(): void {
    this.loading = true;
    this.alertsService
      .getAlerts(this.activeTab, undefined, 200)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((alerts) => {
        this.alerts = alerts;
        this.loading = false;
      });
  }

  acknowledge(alert: Alert): void {
    this.alertsService
      .acknowledge(alert.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.loadAlerts());
  }

  /**
   * "Acknowledge all" (Joel, 2026-10-08). An admin acknowledges across the customer, so no project is
   * sent. A regular user acknowledges the project they have open, so the button names it and the
   * request carries it; with no project open there is nothing it could mean, so it is hidden. The
   * server enforces the same rule.
   */
  get showAckAll(): boolean {
    return this.activeTab === 'open' && this.hasOpenAlerts && (this.isAdmin || !!this.project);
  }

  acknowledgeAll(): void {
    if (!this.isAdmin && !this.project) return;
    this.acknowledging = true;
    this.alertsService
      .acknowledgeAll(this.isAdmin ? undefined : this.project)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.acknowledging = false;
        this.loadAlerts();
      });
  }

  get hasOpenAlerts(): boolean {
    return this.alerts.some((a) => a.state === 'open');
  }

  conditionLabel(alert: Alert): string {
    switch (alert.conditionType) {
      case 'TemperatureThreshold':
        return 'Temperature';
      case 'SensorDisconnected':
        return 'Sensor offline';
      case 'SystemError':
        return 'System error';
      default:
        return alert.conditionType;
    }
  }

  /**
   * One-click into the project that fired an alert.
   *
   * Navigates by the canonical deep link, so following an alert in-app lands in
   * exactly the same place as clicking the emailed version of the same link.
   * Silently does nothing for an alert with no project — the row hides its
   * affordance in that case, and a click that quietly goes nowhere beats one
   * that navigates somewhere wrong.
   */
  openAlertProject(alert: Alert): void {
    const route = alertRoute(alert);
    if (!route) {
      return;
    }
    void this.router.navigate([route.path], { queryParams: route.queryParams });
  }

  /** True when this alert can be followed to a project. */
  canOpenAlert(alert: Alert): boolean {
    return alertRoute(alert) !== null;
  }
}
